import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useLoaderData, useNavigate } from '@remix-run/react';
import {
  IntegrationsManager,
  Player as PlayerWithUI,
  Player,
} from '@brightcove/web-sdk/ui';

import { getBoolValue, isLive } from '~/components/player/utils';
import { getVideoType } from './utils';
import logger from '~/services/logger';
import { PlayNext } from '~/components/player/play-next';
import usePlayerAnalytics from '../hooks/use-player-analytics';
import usePlayerTopBar from '../hooks/use-player-top-bar';
import usePlayNext from '../hooks/use-play-next';
import useBrightcoveAnalytics from '../hooks/use-brightcove-analytics';

import type { Player as PlayerType } from '@brightcove/web-sdk/ui';
import type { PlayerTrackData } from '~/components/player/tracking.ts';
import type { Feed } from '~/services/layout';
import type { loader } from '~/routes/player';
import { AnalyticsSharedVars } from '~/services/analytics/events-names';

declare global {
  interface Window {
    // BrightcoveWebSdkSsaiIntegration is available after loading the index.iife.min.js script
    BrightcoveWebSdkSsaiIntegration: any;
  }
}

function BrightcovePlayer({
  playableItem,
  pluginsData,
  playNextUrl,
}: {
  playableItem: any;
  pluginsData: any;
  playNextUrl: string | null;
}) {
  const [isRunningPlayNext, setIsRunningPlayNext] = useState(false);
  const [playNextFeed, setPlayNextFeed] = useState<Feed | null>(null);
  const playNextFeedRef = useRef<Feed | null>(null);
  const isRunningPlayNextRef = useRef(false);
  const closePlayNextRef = useRef(false);
  const [currentPosition, setCurrentPosition] = useState(0);
  const [duration, setDuration] = useState(0);

  playNextFeedRef.current = playNextFeed;
  isRunningPlayNextRef.current = isRunningPlayNext;

  const {
    brightcovePlayerAccountId,
    brightcovePlayerPolicyKey,
    brightcovePlayerViewerId,
    analyticsSharedVars,
  } = useLoaderData<typeof loader>();

  const playerContainerRef = useRef<HTMLDivElement>(null);

  const [playerInstance, setPlayerInstance] = useState<PlayerType | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (window?.BrightcoveWebSdkSsaiIntegration) {
      IntegrationsManager.registerSsaiIntegrationFactory(
        window.BrightcoveWebSdkSsaiIntegration.SsaiIntegrationFactory
      );
    }

    if (!brightcovePlayerAccountId) return;

    const player: PlayerType = new PlayerWithUI({
      // @ts-ignore - accountId exists
      accountId: brightcovePlayerAccountId,
    });

    if (brightcovePlayerPolicyKey) {
      player.updateConfiguration({
        brightcove: {
          policyKey: brightcovePlayerPolicyKey as string,
          viewerId: brightcovePlayerViewerId,
        },
        integrations: {
          ssai: {
            timeout: 45_000,
            hideOverlays: false,
          },
        },
        playback: { autoplay: true },
      });
    }

    setPlayerInstance(player);

    return () => {
      player.dispose();
    };
  }, [brightcovePlayerAccountId, brightcovePlayerPolicyKey]);

  const loadRemoteVideo = useCallback(
    (source: { url: string; mimeType: string }) => {
      if (playerInstance) {
        playerInstance.loadRemoteVideo(source);
      }
    },
    [playerInstance]
  );

  const loadCloudVideo = useCallback(
    async (brightcoveVideoId: string) => {
      if (playerInstance) {
        const adConfigId = playableItem?.extensions?.['ad_keys'] || null;

        playerInstance.updateConfiguration({
          brightcove: {
            adConfigId,
          },
        });

        const { promise } = playerInstance.getVideoByIdFromPlaybackApi({
          videoId: brightcoveVideoId.toString(),
        });

        try {
          const brightcoveVideoModel = await promise;

          playerInstance.loadBrightcoveVideoModel(brightcoveVideoModel);
        } catch (error) {
          logger.info(`${error}`);
        }
      }
    },
    [playerInstance, playableItem]
  );

  const loadDataSources = useCallback(() => {
    const src = playableItem?.content?.src;
    const brightcoveVideoId = playableItem?.extensions?.brightcove?.video_id;

    if (brightcoveVideoId || !src) {
      loadCloudVideo(brightcoveVideoId || playableItem?.id);
    } else {
      const source = {
        url: src,
        mimeType: getVideoType(src),
      };

      loadRemoteVideo(source);
    }
  }, [loadCloudVideo, loadRemoteVideo, playableItem]);

  const streamType = useMemo(
    () => (isLive(playableItem) ? 'live' : 'vod'),
    [playableItem]
  );
  const isFreeItem =
    playableItem.extensions?.free || playableItem.extensions?.isFree;
  const analyticsCustomProperties = useMemo(
    () =>
      playableItem.extensions?.analyticsCustomProperties &&
      JSON.stringify(playableItem.extensions?.analyticsCustomProperties),
    [playableItem.extensions?.analyticsCustomProperties]
  );

  const trackData: PlayerTrackData = useMemo(
    () => ({
      ...(analyticsSharedVars as AnalyticsSharedVars),
      name: playableItem?.title,
      media_id: playableItem?.id,
      media_type: playableItem.type?.value,
      duration: playableItem.extensions?.duration,
      stream_format: playableItem?.content?.type,
      stream_type: streamType,
      free_or_paid: getBoolValue(isFreeItem) ? 'free' : 'paid',
      analyticsCustomProperties,
    }),
    [
      analyticsSharedVars,
      playableItem?.title,
      playableItem?.id,
      playableItem?.content?.type,
      playableItem.extensions?.duration,
      playableItem.type?.value,
      streamType,
      isFreeItem,
      analyticsCustomProperties,
    ]
  );

  const playNext = (event: any) => {
    if (!playNextUrl || closePlayNextRef.current) return;

    const isClickEvent: boolean = event?.type === 'click';

    if (!isClickEvent) return;

    playerInstance?.detach();

    navigate(`/player?self-link=${encodeURIComponent(playNextUrl)}`, {
      replace: true,
    });
  };

  useEffect(() => {
    if (playerInstance && playerContainerRef.current) {
      playerInstance.attach(playerContainerRef.current);
      loadDataSources();
    }

    return () => {
      setIsRunningPlayNext(false);
      playNextFeedRef.current = null;
      isRunningPlayNextRef.current = false;

      if (!playerInstance) return;

      if (!document.pictureInPictureElement) playerInstance.detach();
    };
  }, [playerInstance, loadDataSources]);

  useEffect(() => {
    if (!playerInstance) return;

    const ErrorEvent = Player.Event?.PlayerError || 'error';

    const onError = (event: any) => {
      const errorMessage =
        event?.detail?.message || event?.message || 'Unknown error';
      const errorCode = event?.detail?.code || event?.code;
      const videoId = playableItem?.extensions?.brightcove?.video_id;
      const playableItemId = playableItem?.id;

      logger.error(
        `BrightcovePlayer: PlayerError - ${errorMessage} (code: ${
          errorCode || 'N/A'
        }, videoId: ${videoId || 'N/A'}, playableItemId: ${
          playableItemId || 'N/A'
        }, timestamp: ${new Date().toISOString()}, event: ${JSON.stringify(
          event
        )})`
      );

      // Also log full event details to console for debugging
      console.error('BrightcovePlayer: Full error event:', event);
    };

    playerInstance.addEventListener(ErrorEvent, onError);

    return () => {
      try {
        playerInstance.removeEventListener(ErrorEvent, onError);
      } catch (error) {
        logger.error(
          `BrightcovePlayer: Error removing error listener - ${error}`
        );
      }
    };
  }, [playerInstance, playableItem]);

  usePlayerAnalytics(playerInstance, trackData);

  usePlayerTopBar(playerInstance, playableItem, trackData);

  useBrightcoveAnalytics(playerInstance, pluginsData, playableItem, trackData);

  usePlayNext(
    playerInstance,
    playNext,
    playNextUrl,
    setIsRunningPlayNext,
    setPlayNextFeed,
    setDuration,
    setCurrentPosition
  );

  return (
    <>
      <div
        className="h-screen overflow-y-hidden overscroll-none scrollbar-hide"
        id="playerContainer"
        ref={playerContainerRef}
      />
      {isRunningPlayNext && playNextFeed && !closePlayNextRef.current && (
        <PlayNext
          playNext={playNext}
          feed={playNextFeed}
          totalDuration={duration}
          position={currentPosition}
          isPostroll={false}
          showPlayNextOnPostroll={true}
          closePlayNextRef={closePlayNextRef}
        />
      )}
    </>
  );
}

export default BrightcovePlayer;
